import {
  AUTHORIZATION_INTENT_METADATA,
  type AuthorizationSendFailure,
} from '@omniwound/auth-contract';
import type { Operation } from '@trpc/client';

import { apiErrorDomainCode, apiErrorStatus } from '@/lib/api-error';

export { newAuthorizationIntentId } from '@omniwound/auth-contract';

/**
 * The per-operation tRPC context key carrying a mutation's authorization intent
 * id from the call site down to the terminating link.
 *
 * It has to be per-operation rather than per-request: `httpBatchLink` puts
 * several procedures in one HTTP POST, so a header set for the request cannot
 * name any single action. Mutations are therefore split onto an unbatched link
 * (see `useAuthRecovery`) and the header is projected from this context.
 */
const INTENT_CONTEXT_KEY = 'authorizationIntentId';

/**
 * Puts one attempt's intent id into an operation's context.
 *
 * The value must be a concrete string, never a getter or thunk: `splitLink`
 * evaluates `carriesAuthorizationIntent` before `httpLink` builds headers, and
 * that predicate tests `typeof === 'string'`. A deferred value would silently
 * route the mutation onto the *batched* link, whose headers carry only
 * authentication — the intent would vanish and the BFF would reject the call.
 */
export function authorizationIntentContext(
  intentId: string,
): Record<string, unknown> {
  return { [INTENT_CONTEXT_KEY]: intentId };
}

/** Projects the operation's intent onto the header the BFF reads it from. */
export function authorizationIntentHeaders(
  op: Operation,
): Record<string, string> {
  const intentId = op.context[INTENT_CONTEXT_KEY];
  if (typeof intentId !== 'string' || !intentId) {
    return {};
  }
  return { [AUTHORIZATION_INTENT_METADATA.intentIdKey]: intentId };
}

/** Whether an operation carries an intent, used to route it off the batch. */
export function carriesAuthorizationIntent(op: Operation): boolean {
  return typeof op.context[INTENT_CONTEXT_KEY] === 'string';
}

/**
 * Normalises a tRPC client error into the facts the intent lifecycle needs.
 *
 * `delivered` hangs off whether a status was observed at all: a tRPC client
 * error raised by a failed `fetch`, an abort, or a timeout carries no
 * `data.httpStatus`, and such a failure says nothing about whether the mutation
 * was applied. Anything with a status did reach the BFF, so its status and
 * stable domain code can be trusted to classify the outcome (ADR 0273).
 */
export function authorizationSendFailure(
  error: unknown,
): AuthorizationSendFailure {
  const httpStatus = apiErrorStatus(error);
  return {
    delivered: httpStatus !== undefined,
    httpStatus,
    domainCode: apiErrorDomainCode(error),
  };
}
